import { useState, useRef, useEffect } from 'react'
import { X, ChevronsUpDown } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { SearchItem } from './SearchMultiSelect'

export type { SearchItem }

interface SearchSingleSelectProps {
  label?: string
  placeholder?: string
  items: SearchItem[]
  value: string           // selected id, empty string for none
  onChange: (id: string) => void
  disabled?: boolean
}

export function SearchSingleSelect({
  label,
  placeholder,
  items,
  value,
  onChange,
  disabled,
}: SearchSingleSelectProps) {
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const selectedItem = items.find((i) => i.id === value) ?? null

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const filtered = items.filter((item) =>
    item.label.toLowerCase().includes(search.toLowerCase())
  )

  function selectItem(item: SearchItem) {
    onChange(item.id)
    setSearch('')
    setOpen(false)
  }

  function clearSelection(e: React.MouseEvent) {
    e.stopPropagation()
    onChange('')
    setSearch('')
    inputRef.current?.focus()
  }

  // What to show in the input: search text if typing, selected label if idle
  const inputDisplayValue = open ? search : (selectedItem?.label ?? '')

  return (
    <div className="space-y-1.5">
      {label && <Label>{label}</Label>}

      <div ref={containerRef} className="relative">
        <div className="relative">
          <Input
            ref={inputRef}
            type="text"
            placeholder={placeholder ?? 'Search…'}
            value={inputDisplayValue}
            disabled={disabled}
            onChange={(e) => {
              setSearch(e.target.value)
              setOpen(true)
            }}
            onFocus={() => {
              setOpen(true)
              if (selectedItem) setSearch('')
            }}
            className="pr-8"
            autoComplete="off"
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-2">
            {selectedItem ? (
              <button
                type="button"
                onClick={clearSelection}
                disabled={disabled}
                className="rounded-sm p-0.5 hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                aria-label="Clear selection"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : (
              <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            )}
          </div>
        </div>

        {open && !disabled && (
          <div className="absolute z-50 top-full mt-1 w-full rounded-md border bg-popover shadow-md max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">No matches.</p>
            ) : (
              filtered.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors text-left ${
                    item.id === value ? 'bg-muted/60 font-medium' : ''
                  }`}
                  onClick={() => selectItem(item)}
                >
                  <span>{item.label}</span>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  )
}
